import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { finalize } from 'rxjs';
import {
  AlertController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
// Components
import { LeafletMapComponent } from '../../leaflet-map/leaflet-map.component';
import { DirectoryTreeComponent } from '../../directory-tree/directory-tree.component';
import { NewRegionCardComponent } from './components/new-region-card/new-region-card.component';
import { NewRegionMapFormComponent } from './components/new-region-map-form/new-region-map-form.component';
// Services
import { RegionMapsService } from 'src/app/services/region-maps.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
// Icons
import { greenWaypoint } from 'src/assets/icons';
import { Icons } from '../icons.model';
// Models
import {
  PlanningItem,
  Region,
  RegionMap,
  MultipleApiResponse,
  RegionRef,
  AutopilotCommand,
  AutopilotSpeedUnitsStringEnum,
  AutopilotAltitudeUnitsStringEnum,
} from '@mocca-technologies/model';

@Component({
  selector: 'app-select-region-area',
  templateUrl: './select-region-area.component.html',
  styleUrls: ['./select-region-area.component.scss'],
})
export class SelectRegionAreaComponent implements OnInit, AfterViewInit {

  // ── Inputs (passed via Ionic modal componentProps) ───────────────────
  /** lat/lng of the last waypoint placed in the active trajectory */
  @Input() lastWaypointLatLng: L.LatLng | null = null;
  /** Bounding box of the full current trajectory (may be invalid/empty) */
  @Input() trajectoryBounds: L.LatLngBounds | null = null;
  /** All waypoints of the trajectory in order (departure → mission → arrival) */
  @Input() trajectoryWaypoints: AutopilotCommand[] = [];

  // ── ViewChildren ─────────────────────────────────────────────────────
  @ViewChild('directoryTreeRegions') directoryTreeRegions: DirectoryTreeComponent;
  @ViewChild('directoryTreeMaps') directoryTreeMaps: DirectoryTreeComponent;
  @ViewChild(LeafletMapComponent) leafletMapComponent: LeafletMapComponent;
  @ViewChild(NewRegionCardComponent) newRegionCardComponent: NewRegionCardComponent;

  // ── Select-tab state ─────────────────────────────────────────────────
  regionsList: PlanningItem[] = [];
  selectedRegion: Region = null;
  regionSelected: string = null;

  // ── Create-tab state ─────────────────────────────────────────────────
  regionMap: RegionMap = null;
  regionMapList: PlanningItem[] = [];
  createdRegionMap: RegionMap = null;
  private regionMapsLoaded = false;

  // ── UI state ──────────────────────────────────────────────────────────
  activeTab: 'select' | 'create' = 'select';
  private lastWaypointMarker: L.Marker | null = null;
  private trajectoryLayer: L.LayerGroup | null = null;

  constructor(
    private regionsService: RegionMapsService,
    private modalController: ModalController,
    private loadingControl: LoadingController,
    private alertController: AlertController,
    private translateService: TranslateService,
    private httpResponseService: HttpResponsesService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.refreshRegionsList();
  }

  ngAfterViewInit(): void {
    // If the HTTP response arrived before the view was initialized (fast local server
    // or cached interceptor), directoryTreeRegions was null in the callback and the
    // tree was never populated. Defer one microtask so we're outside the current CD
    // cycle and avoid NG0100.
    if (this.regionsList.length > 0) {
      Promise.resolve().then(() => {
        if (!this.directoryTreeRegions) return;
        this.directoryTreeRegions.items = this.regionsList;
        if (this.selectedRegion) {
          const item = this.selectedRegion as PlanningItem;
          item.kind = 'region';
          this.directoryTreeRegions.loadItems([item]);
          this.drawOnMap(this.selectedRegion);
          this.fitCamera();
        } else {
          this.directoryTreeRegions.loadItems();
        }
      });
    }
  }

  /** Called by <app-leaflet-map (mapReady)="onMapReady($event)"> */
  onMapReady(map: L.Map): void {
    if (this.newRegionCardComponent) {
      this.newRegionCardComponent.leafletMapComponent = this.leafletMapComponent;
    }
    // Wait past LeafletMapComponent's own invalidateSize (500 ms) and the Ionic modal
    // open animation (~400 ms). Before that, getSize() returns (0,0) which causes
    // getBoundsZoom → -Infinity zoom → NaN in projection → crash.
    setTimeout(() => this.centerOnTrajectory(map), 800);
  }

  // ── Trajectory context ────────────────────────────────────────────────

  private centerOnTrajectory(map: L.Map): void {
    if (!map) return;

    if (this.trajectoryWaypoints?.length > 0) {
      this.drawTrajectoryPreview(map);
    } else {
      // Fallback: no waypoints passed — center on last waypoint if valid
      const wpValid = this.lastWaypointLatLng &&
        isFinite(this.lastWaypointLatLng.lat) && isFinite(this.lastWaypointLatLng.lng);
      if (wpValid) {
        try { map.invalidateSize(false); } catch { /* no-op */ }
        const mapSize = map.getSize();
        if (mapSize.x > 0 && mapSize.y > 0) {
          map.setView(this.lastWaypointLatLng, 14, { animate: true });
        }
        this.placeLastWaypointMarker(map);
      }
    }
  }

  private drawTrajectoryPreview(map: L.Map): void {
    // Remove any previous trajectory overlay
    if (this.trajectoryLayer) {
      this.trajectoryLayer.remove();
      this.trajectoryLayer = null;
    }
    if (this.lastWaypointMarker) {
      this.lastWaypointMarker.remove();
      this.lastWaypointMarker = null;
    }

    const stageColors: Record<string, string> = {
      Departure: '#FF595E',
      Mission: '#8AC926',
      Arrival: '#326CEB',
    };

    const icons = new Icons();
    this.trajectoryLayer = L.layerGroup().addTo(map);

    const validWps = (this.trajectoryWaypoints ?? []).filter(
      (cmd) =>
        cmd?.point?.latitude != null &&
        cmd?.point?.longitude != null &&
        isFinite(cmd.point.latitude) &&
        isFinite(cmd.point.longitude),
    );

    if (validWps.length === 0) return;

    // Draw polyline segments between consecutive waypoints
    for (let i = 0; i < validWps.length - 1; i++) {
      const from = validWps[i];
      const to = validWps[i + 1];
      const color = stageColors[from.stageLabel] ?? '#000000';
      L.polyline(
        [
          L.latLng(from.point.latitude, from.point.longitude),
          L.latLng(to.point.latitude, to.point.longitude),
        ],
        { color, weight: 3, opacity: 0.8 },
      ).addTo(this.trajectoryLayer);
    }

    // Draw markers with stage icons and popups
    const bounds = L.latLngBounds([]);
    validWps.forEach((cmd) => {
      const latlng = L.latLng(cmd.point.latitude, cmd.point.longitude);
      bounds.extend(latlng);

      const iconKey = `${cmd.stageLabel}-${cmd.apCommand}`;
      const icon = icons.getIcon(iconKey) ?? greenWaypoint;

      const popup = `
        <div>
          <strong>Stage: ${cmd.stageLabel}</strong><br>
          <strong>Label: ${cmd.label}</strong><br>
          <strong>Type: ${cmd.apCommand}</strong><br>
          <strong>Speed: ${cmd.speed} ${AutopilotSpeedUnitsStringEnum.mps}</strong><br>
          <strong>Altitude: ${cmd.point.altitude} ${AutopilotAltitudeUnitsStringEnum.meters}</strong>
        </div>`;

      L.marker(latlng, { icon, interactive: true, zIndexOffset: 500 })
        .bindPopup(popup, { className: 'custom-popup' })
        .addTo(this.trajectoryLayer);
    });

    // Fit the map to show the entire trajectory
    if (bounds.isValid()) {
      // A single waypoint produces degenerate bounds (NE === SW); fitBounds would
      // compute a zero-size pixel area → Math.log(0) → -Infinity zoom → NaN crash.
      const isSinglePoint = bounds.getNorthEast().equals(bounds.getSouthWest());
      if (isSinglePoint) {
        try { map.invalidateSize(false); } catch { /* no-op */ }
        map.setView(bounds.getCenter(), 14, { animate: true });
      } else {
        try { map.invalidateSize(false); } catch { /* no-op */ }
        const mapSize = map.getSize();
        if (mapSize.x > 0 && mapSize.y > 0) {
          map.fitBounds(bounds, { padding: [40, 40], animate: true });
        } else {
          map.setView(bounds.getCenter(), 14, { animate: true });
        }
      }
    }
  }

  private placeLastWaypointMarker(map: L.Map): void {
    if (!this.lastWaypointLatLng || !map) return;
    if (!isFinite(this.lastWaypointLatLng.lat) || !isFinite(this.lastWaypointLatLng.lng)) return;

    // Remove any previous marker to avoid duplicates on tab switch
    if (this.lastWaypointMarker) {
      this.lastWaypointMarker.remove();
      this.lastWaypointMarker = null;
    }

    this.lastWaypointMarker = L.marker(this.lastWaypointLatLng, {
      icon: greenWaypoint,
      interactive: false,
      zIndexOffset: 1000,
    }).addTo(map);
  }

  // ── Tab management ────────────────────────────────────────────────────

  switchTab(tab: 'select' | 'create'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;

    if (tab === 'select') {
      // Clear any drawn polygon from create mode
      this.leafletMapComponent?.polygonLayer?.clearLayers();
      if (this.selectedRegion) {
        this.drawOnMap(this.selectedRegion);
        this.fitCamera();
      } else {
        this.centerOnTrajectory(this.leafletMapComponent?.leafletmap);
      }
    } else {
      // Wire the map reference every time we open the create tab.
      // onMapReady may have fired before Ionic modal resolved all ViewChildren,
      // leaving newRegionCardComponent.leafletMapComponent undefined.
      if (this.newRegionCardComponent && this.leafletMapComponent) {
        this.newRegionCardComponent.leafletMapComponent = this.leafletMapComponent;
      }
      // Hide the trajectory preview while drawing a new region
      // Keep trajectoryLayer visible — it serves as a useful reference when drawing.
      // Clear the region preview and center for drawing — use last waypoint for context,
      // zoomed in enough to draw a region (~10 km radius / zoom 13).
      this.leafletMapComponent?.polygonLayer?.clearLayers();
      const createCenter = (
        this.lastWaypointLatLng &&
        isFinite(this.lastWaypointLatLng.lat) &&
        isFinite(this.lastWaypointLatLng.lng)
      )
        ? this.lastWaypointLatLng
        : new L.LatLng(
            this.leafletMapComponent.catCenter.lat,
            this.leafletMapComponent.catCenter.lng,
          );
      this.leafletMapComponent?.leafletmap?.setView(createCenter, 13, { animate: true });
      if (!this.regionMapsLoaded) {
        this.loadRegionMaps();
      }
    }
  }

  // ── Leaflet helpers ───────────────────────────────────────────────────

  fitCamera(): void {
    const bounds = L.latLngBounds([]);
    this.leafletMapComponent.polygonLayer.eachLayer((layer: L.Polygon) => {
      if (layer instanceof L.Polygon) {
        bounds.extend(layer.getBounds());
      }
    });
    // Also include trajectory waypoints so the region and the flight path are both visible
    (this.trajectoryWaypoints ?? []).forEach((cmd) => {
      if (
        cmd?.point?.latitude != null && isFinite(cmd.point.latitude) &&
        cmd?.point?.longitude != null && isFinite(cmd.point.longitude)
      ) {
        bounds.extend(L.latLng(cmd.point.latitude, cmd.point.longitude));
      }
    });
    bounds.isValid() && this.leafletMapComponent.leafletmap.fitBounds(bounds, { padding: [40, 40] });
  }

  drawOnMap(region: Region): void {
    this.leafletMapComponent.polygonLayer.clearLayers();
    const rset = (region.rset as any).map(
      (e) => new L.LatLng(e.latitude, e.longitude),
    );
    this.leafletMapComponent.polygonLayer.addLayer(
      new L.Polygon(rset, { color: 'red' }).bindTooltip(region.name || '', {
        permanent: false,
        sticky: true,
      }),
    );
    this.leafletMapComponent.polygonLayer.addTo(this.leafletMapComponent.leafletmap);
  }

  clearMap(): void {
    this.selectedRegion = null;
    this.regionSelected = null;
    this.leafletMapComponent.clearAllLayers();
    this.leafletMapComponent.polygonLayer.clearLayers();
    // Re-draw trajectory preview and re-fit camera to trajectory, exactly as on open
    const map = this.leafletMapComponent?.leafletmap;
    if (map) {
      if (this.trajectoryWaypoints?.length > 0) {
        this.drawTrajectoryPreview(map);
      } else {
        this.centerOnTrajectory(map);
      }
    }
  }

  // ── Data: regions (Select tab) ────────────────────────────────────────

  async refreshRegionsList(event: CustomEvent | { target: any } = null): Promise<void> {
    const loading = await this.loadingControl.create({
      message: this.translateService.instant('COMMON.LOADING.REGIONS'),
      spinner: 'bubbles',
      cssClass: 'custom-loading',
    });

    await loading.present();

    this.regionsService
      .getRegionsList()
      .pipe(finalize(() => (event as any)?.target?.complete()))
      .subscribe({
        next: (res: HttpResponse<MultipleApiResponse>) => {
          const data = res?.body?.responseObject as RegionMap[];
          this.regionsList = data as PlanningItem[];

          // Mark kinds and find pre-selection in one pass
          let preSelected: Region | null = null;
          this.regionsList.forEach((regionMap: PlanningItem) => {
            regionMap.kind = 'regionMap';
            // buildTree requires a path; fall back to the regionMap name if the server omits it
            if (!regionMap.path) {
              regionMap.path = '/' + (regionMap.name || 'Other');
            }
            regionMap?.regions?.forEach((regionRef: RegionRef) => {
              const region = regionRef.region as Region;
              if (region?.name === this.selectedRegion?.name) {
                preSelected = region;
              }
            });
          });

          if (preSelected) {
            this.selectedRegion = preSelected;
            this.regionSelected = (preSelected as Region).uniqueId;
          }

          // HttpClient runs in Zone.js — no manual detectChanges() needed.
          // Assign items directly before loadItems() so the tree has data immediately.
          if (this.directoryTreeRegions) {
            this.directoryTreeRegions.items = this.regionsList;
            this.cdr.detectChanges();
            if (preSelected) {
              const item = preSelected as PlanningItem;
              item.kind = 'region';
              this.directoryTreeRegions.loadItems([item]);
              this.drawOnMap(preSelected as Region);
              this.fitCamera();
            } else {
              this.directoryTreeRegions.loadItems();
            }
          }

          loading.dismiss();
        },
        error: (_err: HttpErrorResponse) => {
          loading.dismiss();
        },
      });
  }

  selectedRegionInTree(regions: PlanningItem[]): void {
    this.selectedRegion = regions.pop() as Region;
    this.regionSelected = this.selectedRegion?.name;

    if (this.selectedRegion) {
      // The tree returns regionRef.region (the bare Region), which may lack
      // minAltitude/maxAltitude — those live on the parent RegionRef wrapper.
      // Find the matching RegionRef and copy the values onto the selected region.
      outer: for (const rm of this.regionsList as unknown as RegionMap[]) {
        for (const ref of rm.regions ?? []) {
          if (
            ref.region?.uniqueId === this.selectedRegion.uniqueId ||
            ref.region?.name === this.selectedRegion.name
          ) {
            this.selectedRegion.minAltitude = ref.minAltitude ?? this.selectedRegion.minAltitude;
            this.selectedRegion.maxAltitude = ref.maxAltitude ?? this.selectedRegion.maxAltitude;
            break outer;
          }
        }
      }

      this.drawOnMap(this.selectedRegion);
      this.fitCamera();
    }
  }

  // ── Data: region maps (Create tab) ───────────────────────────────────

  async loadRegionMaps(): Promise<void> {
    const loading = await this.loadingControl.create({
      message: this.translateService.instant('COMMON.LOADING.REGIONS'),
      spinner: 'bubbles',
      cssClass: 'custom-loading',
    });

    await loading.present();

    this.regionsService.getRegionsList().subscribe({
      next: (res: HttpResponse<MultipleApiResponse>) => {
        const data = res?.body?.responseObject as RegionMap[];
        this.regionMapList = data as PlanningItem[];
        this.regionMapList.forEach((rm) => {
          rm.kind = 'regionMap';
          if (!rm.path) rm.path = '/' + (rm.name || 'Other');
        });

        if (this.createdRegionMap) {
          const item = this.createdRegionMap as PlanningItem;
          item.kind = 'regionMap';
          if (!this.regionMapList.find((rm) => rm.name === item.name)) {
            this.regionMapList.push(item);
          }
        }

        if (this.directoryTreeMaps) {
          this.directoryTreeMaps.items = this.regionMapList;
          this.cdr.detectChanges();
          if (this.createdRegionMap) {
            this.directoryTreeMaps.loadItems([this.createdRegionMap as PlanningItem]);
          } else {
            this.directoryTreeMaps.loadItems();
          }
        }

        this.regionMapsLoaded = true;
        loading.dismiss();
      },
      error: (_err: HttpErrorResponse) => {
        loading.dismiss();
      },
    });
  }

  selectedRegionMapFromTree(items: PlanningItem[]): void {
    this.regionMap = items.pop() as RegionMap;
  }

  async openRegionMapForm(): Promise<void> {
    const modal = await this.modalController.create({
      component: NewRegionMapFormComponent,
      cssClass: 'add-regionMap-modal',
      componentProps: {
        regionMapList: this.regionMapList,
      },
    });

    await modal.present();

    modal.onDidDismiss().then(({ data }) => {
      if (data) {
        this.regionMap = data;
        this.createdRegionMap = this.regionMap;
        this.regionMapsLoaded = false;
        this.loadRegionMaps();
      }
    });
  }

  // ── Validate & Create ─────────────────────────────────────────────────

  validateRegion(): boolean {
    if (!this.newRegionCardComponent) return false;
    const region = this.newRegionCardComponent.region;
    return !!(region?.name && region?.minAltitude && region?.maxAltitude && region?.polygonLayer);
  }

  async createRegion(): Promise<void> {
    if (!this.validateRegion() || !this.regionMap) {
      const alert = await this.alertController.create({
        header: this.translateService.instant('REGION.INVALID_HEADER'),
        message: this.translateService.instant('REGION.INVALID_MESSAGE'),
        cssClass: 'custom-alert',
        buttons: [{ text: this.translateService.instant('COMMON.BUTTONS.OK'), role: 'cancel' }],
      });
      await alert.present();
      return;
    }

    const r = this.newRegionCardComponent.region;
    const regionRef: RegionRef = {
      regionName: r.name + '-' + Math.random().toString(36).substring(2, 8),
      minAltitude: r.minAltitude,
      maxAltitude: r.maxAltitude,
      region: r,
      regionUUID: '',
      referenceRegionName: '',
    };

    this.regionMap.regions = this.regionMap.regions || [];
    this.regionMap.regions.push(regionRef);

    this.regionsService.createRegion(this.regionMap).subscribe({
      next: (res: HttpResponse<MultipleApiResponse>) => {
        this.httpResponseService.showServerError(res?.status, res?.body?.responseMessage);
        // Populate rset from the drawn polygon so the parent can paint it on the map
        if (r.polygonLayer) {
          const latlngs = (r.polygonLayer.getLatLngs()[0] as L.LatLng[]);
          r.rset = latlngs.map(ll => ({ latitude: ll.lat, longitude: ll.lng })) as any;
        }
        this.modalController.dismiss(r);
      },
      error: (error: HttpErrorResponse) => {
        this.regionMap.regions.pop();
        this.httpResponseService.showServerError(error.status, error?.error);
      },
    });
  }

  // ── Modal actions ─────────────────────────────────────────────────────

  selectRegion(): void {
    this.modalController.dismiss(this.selectedRegion);
  }

  dismiss(): void {
    this.modalController.dismiss(null);
  }
}
